import { GeolocationResponse, GeoPosition } from 'shared-types'

type GeolocationOptions = {
  onSuccess?: (data: GeolocationResponse) => void
  onError?: (error: string) => void
  coordinates?: {
    latitude: number
    longitude: number
  }
}

const getCoordinates = (coordinates?: {
  latitude: number
  longitude: number
}): Promise<GeoPosition> => {
  if (coordinates) {
    return Promise.resolve(coordinates)
  }
  return new Promise<GeoPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        return resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        })
      },
      (error) => {
        let errorMessage
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location unavailable'
            break
          case error.TIMEOUT:
            errorMessage = 'Location request timed out'
            break
          default:
            errorMessage = 'An unknown error occurred'
            break
        }
        reject(new Error(errorMessage))
      }
    )
  })
}

const reverseGeocode = async (
  position: GeoPosition
): Promise<GeolocationResponse> => {
  const geocoder = new window.google.maps.Geocoder()
  const latlng = new window.google.maps.LatLng(
    position.latitude,
    position.longitude
  )
  const results = await geocoder.geocode({ location: latlng })
  if (!results.results[0]) {
    throw new Error('No address found for this location')
  }

  const addressComponents = results.results[0].address_components
  const getComponent = (type: string, preferShortName = false) => {
    const component = addressComponents.find((comp) => {
      return comp.types.includes(type)
    })
    if (!component) {
      return ''
    }

    if (preferShortName && component.short_name) {
      return component.short_name
    }

    return component.long_name
  }

  return {
    position,
    location: {
      country: getComponent('country', true) || getComponent('country'),
      postcode: getComponent('postal_code') || '',
      address: results.results[0].formatted_address,
      suburb: getComponent('locality') || getComponent('sublocality') || '',
      state:
        getComponent('administrative_area_level_1', true) ||
        getComponent('administrative_area_level_1') ||
        '',
    },
  }
}

export const getCurrentLocation = async (
  options: GeolocationOptions = {}
): Promise<GeolocationResponse | null> => {
  const { onSuccess, onError, coordinates } = options

  try {
    const position = await getCoordinates(coordinates)
    const response = await reverseGeocode(position)
    onSuccess?.(response)
    return response
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to get location'
    onError?.(errorMessage)
    return null
  }
}
